import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import DefaultLayout from "../../layouts/DefaultLayout";
import { withRouter } from "react-router-dom";
import { Button, Card, Col, Input, message, Row, Table } from "antd";
import { defaultCurrencyFormat } from "../../../utilities";
import EditableTableCell from "../../table/EditableTableCell";
import claimStatuses from "../../../configs/claim-statuses";

/**
 * Shows claims for quotes.
 */
@inject("context")
@withRouter
@observer
class EditClaim extends Component {
	state = {
		carrierId: "",
		reserve: 0,
		total: 0,
		status: claimStatuses.PENDING_DECISION,
		claimId: 0,
		dataSource: [],
		documents: [],
		messages: [],
	};

	componentDidMount() {
		const { data } = this.props.location;
		const { claimsService } = this.props.context;
		claimsService.updateIsMsgRead(data.claimId);

		if (!data) {
			message.error(`Data is not loaded, please go back and reload the claim`);
		} else this.initializeState(data);
	}

	initializeState = (data) => {
		this.setState({
			carrierId: data.carrierId,
			reserve: data.reserve,
			total: data.total,
			status: data.status,
			claimId: data.claimId,
			reserveCurrency: data.reserveCurrency,
			dataSource: Array.of(data),
			documents: data.documents,
			messages: data.messages,
		});
	};

	render() {
		const { messages, documents, dataSource, claimId } = this.state;

		const claimHolderColumns = [
			{
				title: "Renewal date",
				dataIndex: "renewalDate",
				render: (date) => {
					return new Date(date).toLocaleDateString();
				},
			},
			{
				title: "Policy number",
				dataIndex: "policyNumber",
			},
			{
				title: "First name",
				dataIndex: "first_name",
			},
			{
				title: "Last name",
				dataIndex: "last_name",
			},
			{
				title: "Phone number",
				dataIndex: "phoneNumber",
			},
			{
				title: "Address 1",
				dataIndex: "address1",
			},
			{
				title: "Address 2",
				dataIndex: "address2",
			},
			{
				title: "City",
				dataIndex: "city",
			},
			{
				title: "Postcode",
				dataIndex: "post_code",
			},
			{
				title: "Total claimed",
				dataIndex: "totalClaimed",
				render: (n) => {
					return defaultCurrencyFormat(n);
				},
			},
			{
				title: "FNOL date",
				dataIndex: "fnolDate",
				render: (date) => {
					return new Date(date).toLocaleDateString();
				},
			},
		];

		const claimColumns = [
			{
				title: "Carrier id",
				dataIndex: "carrierId",
				render: (carrierId, record) => {
					return (
						<EditableTableCell
							recordId={record.id}
							handleSave={this.handleSaveEditCarrierId}
							value={this.state.carrierId}
						/>
					);
				},
			},
			{
				title: "Claim status",
				dataIndex: "status",
				render: () => {
					return (
						<select onChange={this.onChangeSelected} value={this.state.status}>
							{Object.keys(claimStatuses).map((status, index) => {
								return (
									<option key={index} value={status}>
										{status}
									</option>
								);
							})}
						</select>
					);
				},
			},
			{
				title: "Reserve",
				dataIndex: "reserve",
				render: (reserve, record) => {
					return (
						<EditableTableCell
							recordId={record.id}
							handleSave={this.handleSaveEditReserve}
							value={defaultCurrencyFormat(this.state.reserve)}
						/>
					);
				},
			},
			{
				title: "Reserve Currency",
				dataIndex: "reserveCurrency",
			},
			{
				title: "Total",
				dataIndex: "total",
				render: (total, record) => {
					return (
						<EditableTableCell
							recordId={record.id}
							handleSave={this.handleSaveEditTotal}
							value={defaultCurrencyFormat(this.state.total)}
						/>
					);
				},
			},
			{
				title: "Actions",
				render: (field, record) => {
					return (
						<Button
							type="primary"
							onClick={() => this.handleSaveChangesButtonClick(record)}
						>
							Save Changes
						</Button>
					);
				},
			},
		];

		const claimMessagesColumns = [
			{
				title: "Message",
				dataIndex: "content",
			},
			{
				title: "Sent By",
				dataIndex: "sentBy",
			},
			{
				title: "Sent At",
				dataIndex: "sentAt",
				sorter: {
					compare: (a, b) => a.sentAt.valueOf() > b.sentAt.valueOf(),
				},
				defaultSortOrder: "descend",
				render: (date) => {
					return new Date(date).toLocaleString();
				},
			},
		];

		const claimDocumentsColumns = [
			{
				title: "Name",
				dataIndex: "name",
			},
			{
				title: "Description",
				dataIndex: "description",
			},
			{
				title: "Content Type",
				dataIndex: "contentType",
			},
			{
				title: "Uploaded At",
				dataIndex: "uploadedAt",
				sorter: {
					compare: (a, b) => a.uploadedAt.valueOf() > b.uploadedAt.valueOf(),
				},
				defaultSortOrder: "descend",
				render: (date) => {
					return new Date(date).toLocaleString();
				},
			},
			{
				title: "Actions",
				render: (field, record) => {
					return (
						<Button
							type="primary"
							onClick={() => this.handleDownloadDocumentButtonClick(record)}
						>
							Download
						</Button>
					);
				},
			},
		];

		const descriptionColumns = [
			{
				title: "Description",
				dataIndex: "description",
			},
		];

		const { TextArea } = Input;

		return (
			<DefaultLayout>
				<Row>
					<Col xs={24}>
						<Card title={<h1>Policy holder info</h1>}>
							<Row>
								<Col xs={24}>
									<Table
										rowKey="claimId"
										columns={claimHolderColumns}
										dataSource={dataSource}
										loading={!dataSource.length}
										pagination={false}
									/>
								</Col>
							</Row>
						</Card>
					</Col>
					<Col xs={24}>
						<Card title={<h1>Claim info</h1>}>
							<Row>
								<Col xs={24}>
									<Table
										rowKey="claimId"
										columns={descriptionColumns}
										dataSource={dataSource}
										loading={!dataSource.length}
										pagination={false}
									/>
								</Col>
								<Col xs={24}>
									<Table
										rowKey="claimId"
										columns={claimColumns}
										dataSource={dataSource}
										loading={!dataSource.length}
										pagination={false}
									/>
								</Col>
							</Row>
						</Card>
					</Col>
					<Col xs={24}>
						<Card title={<h1>Documents</h1>}>
							<Row>
								<Col xs={24}>
									<Table
										rowKey="id"
										columns={claimDocumentsColumns}
										dataSource={documents}
										pagination={false}
									/>
								</Col>
							</Row>
						</Card>
					</Col>
					<Col xs={24}>
						<Card title={<h1>Send a new message</h1>}>
							<Row>
								<Col xs={20}>
									<TextArea id="message" rows={4} />
								</Col>
								<Col xs={4}>
									<Button
										style={{
											marginLeft: "15px",
										}}
										onClick={() => this.handleSendMessageButtonClick(claimId)}
									>
										Send message
									</Button>
								</Col>
							</Row>
						</Card>
					</Col>
					<Col xs={24}>
						<Card title={<h1>Messages Log</h1>}>
							<Row>
								<Col xs={24}>
									<Table
										rowKey="id"
										columns={claimMessagesColumns}
										dataSource={messages}
										pagination={false}
									/>
								</Col>
							</Row>
						</Card>
					</Col>
				</Row>
			</DefaultLayout>
		);
	}

	handleSaveChangesButtonClick = async (record) => {
		const { claimId } = record;
		if (claimId) {
			const claimData = {
				id: claimId,
				carrierId: this.state.carrierId,
				reserve: this.state.reserve,
				total: this.state.total,
				status: this.state.status,
			};
			const { applicationService, claimsService } = this.props.context;
			const { username } = applicationService.userProfile;
			await claimsService.saveClaimData(claimData, username);
		}
	};

	handleDownloadDocumentButtonClick = async (record) => {
		const { claimDocumentService } = this.props.context;
		const { name } = record;
		if (name) await claimDocumentService.downloadClaimDocument(name);
	};

	handleSendMessageButtonClick = async (claimId) => {
		if (!claimId) {
			message.error(`Data is not loaded, please go back and reload the claim`);
			return;
		}
		const { applicationService, claimsService } = this.props.context;
		const { username } = applicationService.userProfile;
		const messageTextArea = document.getElementById("message");
		const claimMessage = {
			content: messageTextArea.value,
			sentBy: username,
			claim: {
				id: claimId,
			},
		};
		await claimsService.sendClaimMessage(claimMessage);
		messageTextArea.value = "";
		const messages = await claimsService.fetchClaimMessages(claimId);
		this.setState({
			messages: messages,
		});
	};

	handleSaveEditCarrierId = async (claimId, edit) => {
		this.setState({
			carrierId: edit,
		});
	};

	handleSaveEditReserve = async (claimId, edit) => {
		const reserve = this.removeCurrency(edit);
		this.setState({
			reserve: reserve,
		});
	};

	handleSaveEditTotal = async (claimId, edit) => {
		const total = this.removeCurrency(edit);
		this.setState({
			total: total,
		});
	};

	removeCurrency(number) {
		return parseFloat(number.replace(/[^\d.]*/g, ""));
	}

	onChangeSelected = async (e) => {
		this.setState({
			status: e.target.value,
		});
	};
}

export default EditClaim;
